import { Box, SxProps, Theme, Typography } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface ColorfulImagePlaceholderProps {
    width?: number;
    height?: number;
    text?: string;
    sx?: object;
    iconSx?: SxProps<Theme>;
    textSx?: SxProps<Theme>;
    /** @default neutral — dark surface; use colorful only where a bright placeholder is intentional */
    variant?: 'neutral' | 'colorful';
}

export function NoImage({
    width = 400,
    height = 300,
    text = 'No image',
    sx = {},
    iconSx,
    textSx,
    variant = 'neutral',
}: ColorfulImagePlaceholderProps) {
    const { darkModeEnabled } = useThemeContext();
    const isColorful = variant === 'colorful';

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width,
                height,
                borderRadius: 2,
                overflow: 'hidden',
                ...(isColorful
                    ? {
                          background: darkModeEnabled
                              ? 'linear-gradient(135deg, #9c27b0 0%, #e91e63 50%, #ff9800 100%)'
                              : 'linear-gradient(135deg, #e1bee7 0%, #f8bbd9 50%, #ffe0b2 100%)',
                      }
                    : {
                          bgcolor: '#1a1919',
                          border: '1px solid rgba(255,255,255,0.08)',
                          backgroundImage:
                              'radial-gradient(circle at 50% 35%, rgba(255,255,255,0.06) 0%, transparent 55%)',
                      }),
                ...sx,
            }}>
            {isColorful ? (
                <Box sx={{ position: 'absolute', inset: 0, opacity: 0.2 }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            width: 32,
                            height: 32,
                            bgcolor: 'white',
                            borderRadius: '50%',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 48,
                            right: 32,
                            width: 16,
                            height: 16,
                            bgcolor: 'white',
                            borderRadius: '50%',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 32,
                            left: 48,
                            width: 24,
                            height: 24,
                            bgcolor: 'white',
                            borderRadius: '50%',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 16,
                            right: 16,
                            width: 12,
                            height: 12,
                            bgcolor: 'white',
                            borderRadius: '50%',
                        }}
                    />
                </Box>
            ) : null}

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isColorful ? 'white' : 'rgba(255,255,255,0.55)',
                }}>
                <Box
                    sx={{
                        bgcolor: isColorful ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                        backdropFilter: isColorful ? 'blur(8px)' : 'none',
                        borderRadius: '50%',
                        p: 2,
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <ImageIcon
                        sx={{
                            fontSize: 32,
                            color: isColorful ? 'white' : 'rgba(255,255,255,0.4)',
                            ...iconSx,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontWeight: 600,
                        textAlign: 'center',
                        px: 2,
                        textShadow: isColorful ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        letterSpacing: '0.02em',
                        ...textSx,
                    }}>
                    {text}
                </Typography>
            </Box>

            {isColorful ? <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.1)' }} /> : null}
        </Box>
    );
}
